import LegalPage from "@/pages/LegalPage";

const SECTION_HEADING = "font-display text-xl md:text-2xl font-bold text-white tracking-tight mt-10 mb-3";
const PARAGRAPH = "text-[#CBD5E1] leading-relaxed text-[15px] md:text-base";
const LIST = "list-disc pl-6 mt-3 space-y-2 text-[#CBD5E1] text-[15px] leading-relaxed marker:text-[#14B8A6]";

export default function PrivacyPolicy() {
  return (
    <LegalPage
      testid="privacy-policy-page"
      title="Privacy Policy"
      subtitle="How tweakmycreditreport.com collects, uses, stores, and protects your information — and the rights you have over that data."
      updated="December 2025"
    >
      <p className={PARAGRAPH}>
        This Privacy Policy explains how tweakmycreditreport.com ("we," "us," or "our") collects,
        uses, and protects information you provide when you visit our website, sign up for our free
        guides, or purchase our digital products. By using this site, you consent to the practices
        described below.
      </p>

      <h2 className={SECTION_HEADING}>1. Information We Collect</h2>
      <p className={PARAGRAPH}>
        We collect only the information reasonably necessary to deliver our products, respond to you,
        and improve the site. The categories of information we collect include:
      </p>
      <ul className={LIST}>
        <li>
          <strong className="text-white">Lead-magnet & newsletter data:</strong> When you request the
          free "Credit Repair Jumpstart" guide or subscribe to updates, we collect your email address
          and (optionally) your first name.
        </li>
        <li>
          <strong className="text-white">Purchase data:</strong> When you buy a product, we collect
          your email address and the transaction metadata (session ID, amount, currency, product
          purchased, timestamps) so we can deliver your order and provide support.
        </li>
        <li>
          <strong className="text-white">Payment information:</strong> Card, bank, and billing
          details are collected and processed directly by our payment processor, PayPal. We never
          see, store, or have access to your full card number, CVC, or bank credentials.
        </li>
        <li>
          <strong className="text-white">Technical data:</strong> Standard web logs (IP address,
          browser type, referring page, pages viewed, and timestamps) collected automatically to
          operate and secure the site.
        </li>
        <li>
          <strong className="text-white">Communications:</strong> Any message you send us (e.g.
          support emails) and our responses.
        </li>
      </ul>
      <p className={`${PARAGRAPH} mt-4`}>
        We do not knowingly collect Social Security numbers, full credit reports, or other sensitive
        financial-identity documents through this website. Please do not send such information to us.
      </p>

      <h2 className={SECTION_HEADING}>2. How We Use Your Information</h2>
      <p className={PARAGRAPH}>We use the information we collect to:</p>
      <ul className={LIST}>
        <li>Deliver the products, guides, and templates you request or purchase.</li>
        <li>Process payments, confirm orders, and issue receipts.</li>
        <li>Provide customer support and respond to questions.</li>
        <li>Send occasional product updates, new resources, and educational content (you can
          unsubscribe from marketing emails at any time).</li>
        <li>Monitor, secure, and improve the site (e.g., prevent fraud, diagnose bugs).</li>
        <li>Comply with applicable law, respond to lawful requests, and enforce our Terms.</li>
      </ul>

      <h2 className={SECTION_HEADING}>3. Payment Processing (PayPal)</h2>
      <p className={PARAGRAPH}>
        All payments on tweakmycreditreport.com are processed securely by{" "}
        <a href="https://www.paypal.com" target="_blank" rel="noreferrer" className="text-[#14B8A6] hover:underline">
          PayPal, Inc.
        </a>{" "}
        PayPal is a PCI-DSS compliant payment provider that handles billions of transactions
        globally. Your payment card, bank, or PayPal balance details are transmitted directly from
        your browser to PayPal over an encrypted (HTTPS/TLS) connection. We receive only the
        transaction reference, your payer email, amount paid, and order metadata — never your full
        card number, CVC, or bank credentials. PayPal's handling of your information is governed by
        PayPal's own{" "}
        <a href="https://www.paypal.com/us/legalhub/privacy-full" target="_blank" rel="noreferrer" className="text-[#14B8A6] hover:underline">
          Privacy Statement
        </a>.
      </p>

      <h2 className={SECTION_HEADING}>4. Cookies & Similar Technologies</h2>
      <p className={PARAGRAPH}>
        We use a limited set of cookies and similar browser-storage technologies to operate the site
        and understand usage. Specifically:
      </p>
      <ul className={LIST}>
        <li>
          <strong className="text-white">Essential cookies</strong> that keep the site functional
          (e.g., remembering your checkout session, preventing duplicate submissions).
        </li>
        <li>
          <strong className="text-white">Analytics cookies</strong> that help us understand how the
          site is used in aggregate (pages visited, conversion events). These do not identify you
          personally.
        </li>
        <li>
          <strong className="text-white">Third-party cookies</strong> set by PayPal when you
          initiate checkout, used by PayPal for fraud prevention and session management.
        </li>
      </ul>
      <p className={`${PARAGRAPH} mt-4`}>
        You can control or delete cookies through your browser settings at any time. Disabling
        essential cookies may prevent checkout or lead-magnet delivery from working.
      </p>

      <h2 className={SECTION_HEADING}>5. How We Share Your Information</h2>
      <p className={PARAGRAPH}>
        We do <strong className="text-white">not</strong> sell your personal information. We share it
        only with vetted service providers that help us run the site and fulfill your orders:
      </p>
      <ul className={LIST}>
        <li><strong className="text-white">PayPal</strong> — payment processing.</li>
        <li><strong className="text-white">Email delivery providers</strong> — to deliver your
          lead-magnet guide, purchase receipts, and product downloads.</li>
        <li><strong className="text-white">Hosting & analytics providers</strong> — to operate the
          website and understand aggregate usage.</li>
        <li><strong className="text-white">Legal authorities</strong> — when required by law, court
          order, or to protect our rights.</li>
      </ul>

      <h2 className={SECTION_HEADING}>6. Data Retention</h2>
      <p className={PARAGRAPH}>
        We retain lead, customer, and transaction data for as long as reasonably necessary to provide
        our products and comply with legal, tax, and accounting obligations. You may request deletion
        of your personal data at any time (see Section 8).
      </p>

      <h2 className={SECTION_HEADING}>7. Security</h2>
      <p className={PARAGRAPH}>
        We protect your information with industry-standard measures including encrypted (HTTPS)
        transport, restricted access to our databases, and vetted third-party processors. No method
        of transmission or storage is 100% secure, and we cannot guarantee absolute security.
      </p>

      <h2 className={SECTION_HEADING}>8. Your Rights (CCPA, GDPR & Similar Laws)</h2>
      <p className={PARAGRAPH}>
        Depending on where you live, you may have the following rights regarding your personal
        information:
      </p>
      <ul className={LIST}>
        <li>
          <strong className="text-white">Right to know / access</strong> the personal information we
          hold about you.
        </li>
        <li>
          <strong className="text-white">Right to correct</strong> inaccurate personal information.
        </li>
        <li>
          <strong className="text-white">Right to delete</strong> your personal information, subject
          to legal exceptions (e.g., tax-retention obligations).
        </li>
        <li>
          <strong className="text-white">Right to portability</strong> — to receive a copy of your
          data in a portable format.
        </li>
        <li>
          <strong className="text-white">Right to opt out of sale/sharing</strong> — we do not sell
          your information, but you may confirm that in writing.
        </li>
        <li>
          <strong className="text-white">Right to object</strong> to certain uses (e.g., marketing).
        </li>
        <li>
          <strong className="text-white">Right to non-discrimination</strong> — we will not deny
          service or charge different prices for exercising your rights.
        </li>
      </ul>
      <p className={`${PARAGRAPH} mt-4`}>
        <strong className="text-white">GDPR</strong> (EU/UK residents): where we rely on your consent
        (e.g., marketing emails), you may withdraw it at any time by unsubscribing. Our lawful bases
        for processing include performance of a contract (order fulfillment), legitimate interests
        (site security, analytics), consent (marketing), and legal obligation (tax records).
      </p>
      <p className={`${PARAGRAPH} mt-4`}>
        <strong className="text-white">CCPA / CPRA</strong> (California residents): we do not sell or
        share personal information for cross-context behavioral advertising. You may exercise any of
        the rights above by contacting us. You may also designate an authorized agent to make
        requests on your behalf.
      </p>
      <p className={`${PARAGRAPH} mt-4`}>
        To exercise any right, email us at{" "}
        <a href="mailto:info@tweakmycreditreport.com" className="text-[#14B8A6] hover:underline">
          info@tweakmycreditreport.com
        </a>{" "}
        from the address associated with your account and tell us which right you wish to exercise.
        We will respond within the timeframe required by applicable law.
      </p>

      <h2 className={SECTION_HEADING}>9. Children's Privacy</h2>
      <p className={PARAGRAPH}>
        The site is not directed to children under 16, and we do not knowingly collect information
        from children under 16. If you believe a child has provided us with information, contact us
        and we will delete it.
      </p>

      <h2 className={SECTION_HEADING}>10. International Transfers</h2>
      <p className={PARAGRAPH}>
        Our servers and service providers may be located in the United States. If you access the site
        from outside the US, your information will be transferred to and processed in the US, which
        may have different data-protection laws than your country.
      </p>

      <h2 className={SECTION_HEADING}>11. Changes to This Policy</h2>
      <p className={PARAGRAPH}>
        We may update this Privacy Policy from time to time. We will post the updated policy here
        with a revised "Last updated" date. Material changes will be highlighted where practical.
      </p>

      <h2 className={SECTION_HEADING}>12. Contact Us</h2>
      <p className={PARAGRAPH}>
        Questions, requests, or complaints about this Privacy Policy or our data practices? Reach us
        at{" "}
        <a href="mailto:info@tweakmycreditreport.com" className="text-[#14B8A6] hover:underline">
          info@tweakmycreditreport.com
        </a>
        .
      </p>
    </LegalPage>
  );
}
